import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const COLLAPSED_STORAGE_KEY = 'adminSidebarCollapsed';

interface AdminSidebarContextType {
  showMobileSidebar: boolean;
  setShowMobileSidebar: (show: boolean) => void;
  toggleMobileSidebar: () => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

const AdminSidebarContext = createContext<AdminSidebarContextType | null>(null);

const readCollapsedPreference = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const AdminSidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(readCollapsedPreference);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) {
      setShowMobileSidebar(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, isCollapsed ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [isCollapsed]);

  const toggleMobileSidebar = useCallback(() => {
    setShowMobileSidebar(prev => !prev);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  return (
    <AdminSidebarContext.Provider
      value={{
        showMobileSidebar,
        setShowMobileSidebar,
        toggleMobileSidebar,
        isCollapsed,
        toggleCollapsed,
      }}
    >
      {children}
    </AdminSidebarContext.Provider>
  );
};

export const useAdminSidebar = () => useContext(AdminSidebarContext);
